import * as React from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { useEffect, useState } from "react";
import Authapi from "../../Services/Authapi";
import "./tablefilter.css";
import ls from "local-storage";
import { DataGrid } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { MdAddBox, MdCalendarToday } from "react-icons/md";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
// import Switch from "react-switch";
import { ThemeContext } from '../../../ThemeContext';
import ExportButton from "../../Layouts/ExportButton";
import {
  CommonTablePagination,
  getCommonPaginationSx,
} from "../../Common/commonPagination";
import { isAfterTimeThreshold } from "../../../utils/attendanceStatus";

const isAdminOnLeaveTicketPage = ls("roles") === "Super Admin";

const getBase64ImageFromUrl = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Helper: convert "7 hours 4 minutes" -> seconds
const parseDurationToSeconds = (value) => {
  if (!value || typeof value !== 'string') return 0;
  const sanitized = value.trim();
  if (!sanitized || sanitized.toLowerCase() === 'n/a' || sanitized === '-') return 0;
  const lower = sanitized.toLowerCase();
  const hourMatch = lower.match(/(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|hr|h)\b/);
  const minuteMatch = lower.match(/(\d+(?:\.\d+)?)\s*(?:minutes?|mins?|min|m)\b/);
  const secondMatch = lower.match(/(\d+(?:\.\d+)?)\s*(?:seconds?|secs?|sec|s)\b/);
  if (hourMatch || minuteMatch || secondMatch) {
    const hours = hourMatch ? parseFloat(hourMatch[1]) : 0;
    const minutes = minuteMatch ? parseFloat(minuteMatch[1]) : 0;
    const seconds = secondMatch ? parseFloat(secondMatch[1]) : 0;
    return Math.round(hours * 3600 + minutes * 60 + seconds);
  }
  if (sanitized.includes(':')) {
    const parts = sanitized.split(':').map(p => parseInt(p, 10));
    if (parts.some(isNaN)) return 0;
    if (parts.length === 3) { const [h, m, s] = parts; return (h * 3600) + (m * 60) + s; }
    if (parts.length === 2) { const [m, s] = parts; return (m * 60) + s; }
  }
  const numeric = parseFloat(sanitized);
  if (!isNaN(numeric)) return Math.round(numeric * 60);
  return 0;
};

const getContrastYIQ = (hexcolor) => {
  if (!hexcolor) return 'black';
  let r, g, b;
  if (hexcolor.startsWith('#')) {
    const hex = hexcolor.replace('#', '');
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else {
      r = parseInt(hex.substr(0, 2), 16);
      g = parseInt(hex.substr(2, 2), 16);
      b = parseInt(hex.substr(4, 2), 16);
    }
  } else if (hexcolor.startsWith('rgb')) {
    const rgb = hexcolor.match(/\d+/g);
    r = parseInt(rgb[0]);
    g = parseInt(rgb[1]);
    b = parseInt(rgb[2]);
  } else {
    return 'black';
  }
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? 'black' : 'white';
};

const getWorkingHourThresholds = (workingHoursData) => {
  return {
    halfDayHours: Number(workingHoursData?.half_day_hours) || 5,
    earlyLeaveMinHrs: Number(workingHoursData?.early_leave_min_hours) || 7,
    earlyLeaveAllowedDays: Number(workingHoursData?.early_leave_allowed_days) || 3,
    fullDayHours: Number(workingHoursData?.full_day_hours) || 8,
  };
};

const normalizeTicketDate = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();

  if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
    return trimmed.split('-').reverse().join('-');
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;

  return parsed.toISOString().slice(0, 10);
};

const getApprovedHalfDayLeaveLookup = (leaveTickets = [], authUserId) => {
  const approvedDates = new Set();

  leaveTickets.forEach((ticket) => {
    if (String(ticket?.user_id) !== String(authUserId)) return;
    if (String(ticket?.status || '').trim().toLowerCase() !== 'approved') return;
    if (Number(ticket?.no_of_days) !== 0.5) return;

    let range = ticket?.date_range;

    if (typeof range === 'string') {
      range = range.split(/\s+to\s+/i);
    }

    if (!Array.isArray(range) || !range[0]) return;

    const normalizedDate = normalizeTicketDate(range[0]);
    if (normalizedDate) {
      approvedDates.add(normalizedDate);
    }
  });

  return approvedDates;
};

const computeDayTypeFromRules = ({
  initialDayType,
  attendance,
  workSeconds,
  employeeKey,
  thresholds,
  earlyLeaveCountMap,
  isApprovedHalfDay,
  firstInTime,
}) => {
  const normalizedAttendance = String(attendance || '').trim().toLowerCase();

  if (
    workSeconds <= 0 ||
    ['absent', 'leave', 'week off', 'holiday'].includes(normalizedAttendance)
  ) {
    return initialDayType;
  }

  const halfDaySeconds = thresholds.halfDayHours * 3600;
  const earlyLeaveSeconds = thresholds.earlyLeaveMinHrs * 3600;
  const fullDaySeconds = thresholds.fullDayHours * 3600;
  const lateThresholdStr = thresholds.lateThreshold || '10:30:00';

  if (isApprovedHalfDay) {
    return 'Half-day';
  }

  // 1. Clock-in AT OR AFTER 12:00 PM -> Always Half-day (or Short Hours if < 5h)
  if (isAfterTimeThreshold(firstInTime, '12:00:00', true)) {
    return workSeconds >= halfDaySeconds ? 'Half-day' : 'Short Hours';
  }

  // 2. Clock-in AFTER late threshold (10:30 AM) up to 12:00 PM
  if (isAfterTimeThreshold(firstInTime, lateThresholdStr)) {
    if (workSeconds >= earlyLeaveSeconds) {
      if (!earlyLeaveCountMap[employeeKey]) {
        earlyLeaveCountMap[employeeKey] = 0;
      }
      earlyLeaveCountMap[employeeKey]++;

      return earlyLeaveCountMap[employeeKey] <= thresholds.earlyLeaveAllowedDays
        ? 'Left Early'
        : 'Half-day';
    }

    if (workSeconds >= halfDaySeconds) {
      return 'Half-day';
    }

    return 'Short Hours';
  }

  // 3. Normal Clock-in (On-time, before or at 10:30 AM)
  if (workSeconds >= fullDaySeconds) {
    return 'Full-day';
  }

  if (workSeconds >= earlyLeaveSeconds) {
    if (!earlyLeaveCountMap[employeeKey]) {
      earlyLeaveCountMap[employeeKey] = 0;
    }
    earlyLeaveCountMap[employeeKey]++;

    return earlyLeaveCountMap[employeeKey] <= thresholds.earlyLeaveAllowedDays
      ? 'Left Early'
      : 'Half-day';
  }

  if (workSeconds >= halfDaySeconds) {
    return 'Half-day';
  }

  return 'Short Hours';
};

// Helper: apply settings-based dayType logic (same as Attendance.js)
const computeDayTypeForItems = (items, workingHoursData, approvedHalfDayLookup = new Set()) => {
  const thresholds = getWorkingHourThresholds(workingHoursData);

  // Sort by date ascending so count is correct
  const sorted = [...items].sort((a, b) => {
    const dateA = a.date ? a.date.split('-').reverse().join('-') : '';
    const dateB = b.date ? b.date.split('-').reverse().join('-') : '';
    return new Date(dateA) - new Date(dateB);
  });

  const earlyLeaveCountMap = {};

  const processed = sorted.map((item) => {
    const workSeconds = parseDurationToSeconds(item.hours);
    const employeeKey = item.name || item.firstName || item.userId || 'unknown';
    const normalizedItemDate = normalizeTicketDate(item.date);
    const dayType = computeDayTypeFromRules({
      initialDayType: item.dayType,
      attendance: item.attendance,
      workSeconds,
      employeeKey,
      thresholds,
      earlyLeaveCountMap,
      isApprovedHalfDay: normalizedItemDate ? approvedHalfDayLookup.has(normalizedItemDate) : false,
      firstInTime: item.first_in_time || item.firstInTime || item.in_time || '',
    });

    return { ...item, dayType };
  });

  // Restore original descending date order
  return processed.sort((a, b) => {
    const dateA = a.date ? a.date.split('-').reverse().join('-') : '';
    const dateB = b.date ? b.date.split('-').reverse().join('-') : '';
    return new Date(dateB) - new Date(dateA);
  });
};

const formatDurationText = (value) => {
  if (!value || typeof value !== 'string' || value === '-' || value.toLowerCase() === 'n/a') return value;
  return value
    .replace(/\bhours?\b/gi, 'Hr')
    .replace(/\bminutes?\b/gi, 'Min')
    .replace(/\bhrs?\b/gi, 'Hr')
    .replace(/\bmins?\b/gi, 'Min');
};

const Tableforfilter = () => {
  const { darkMode } = React.useContext(ThemeContext);
  const initialDate = new Date();
  const initialMonthYear = `${initialDate.getFullYear()}-${String(initialDate.getMonth() + 1).padStart(2, "0")}`;
  const monthInputRef = React.useRef(null);
  const [totalDurationTime, setTotalDurationTime] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [leaveTickets, setLeaveTickets] = useState([]);
  const [leaveBalanceSummary, setLeaveBalanceSummary] = useState(null);
  const [rawAttendanceRows, setRawAttendanceRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isMonthSwitching, setIsMonthSwitching] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  // const [showButton, setShowButton] = useState(false);
  const [statusColors, setStatusColors] = useState({});
  const [workingHoursSettings, setWorkingHoursSettings] = useState(null);
  const [selectedMonthYear, setSelectedMonthYear] = useState(initialMonthYear);
  const authUser = ls("user") || {};
  const approvedHalfDayLookup = React.useMemo(
    () => getApprovedHalfDayLeaveLookup(leaveTickets, authUser.id),
    [leaveTickets, authUser.id]
  );

  const getAttendanceCacheKey = (month, year) =>
    `dashboardAttendance:${authUser.id || "guest"}:${isAdminOnLeaveTicketPage ? "admin" : "user"}:${month}-${year}`;

  const handleClearSearch = () => {
    setSearchQuery("");
    setPage(0);
    setTotalDurationTime(originalData);
    setSuggestions([]);
  };

  const formatLeaveBalanceCount = (value) => {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return value || 0;
    return Number.isInteger(numericValue) ? numericValue : numericValue.toFixed(1);
  };

  const formatAttendanceData = (rawItems = []) =>
    rawItems.map((item, index) => ({
      id: index,
      name: item.name || "",
      firstName: item.firstName || "",
      date: item.date,
      hours: item.hours,
      breakDuration: item.break_duration,
      attendance: item.attendance,
      dayType: item.dayType,
      firstInTime: item.first_in_time || item.firstInTime || item.in_time || item.first_in || "",
      userId: item.userId,
    }));

  const applyAttendanceRows = (rows, queryOverride = "") => {
    const normalizedQuery = (queryOverride || "").toLowerCase();
    setPage(0);
    setOriginalData(rows);
    setTotalDurationTime(
      normalizedQuery
        ? rows.filter((item) => (
          item.name.toLowerCase().includes(normalizedQuery) ||
          item.firstName.toLowerCase().includes(normalizedQuery)
        ))
        : rows
    );
  };

  const readAttendanceCache = (month, year) => {
    try {
      const cached = sessionStorage.getItem(getAttendanceCacheKey(month, year));
      if (!cached) return null;
      const parsed = JSON.parse(cached);
      if (!Array.isArray(parsed)) return null;
      if (parsed.some((item) => item.firstInTime === undefined)) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  };

  const writeAttendanceCache = (month, year, rows) => {
    try {
      sessionStorage.setItem(getAttendanceCacheKey(month, year), JSON.stringify(rows));
    } catch (error) {
      // Ignore cache write failures.
    }
  };

  const fetchAttendanceData = async (
    month,
    year,
    workingHoursDataOverride = workingHoursSettings,
    queryOverride = searchQuery,
    showLoader = true
  ) => {
    if (showLoader) {
      setIsLoading(true);
    }
    setSuggestions([]);

    try {
      const attendancePromise = isAdminOnLeaveTicketPage
        ? Authapi.AllgetAttandance(month, year)
        : Authapi.getAttandance(month, year);

      const workingHoursPromise = workingHoursDataOverride
        ? Promise.resolve({ data: workingHoursDataOverride })
        : Authapi.getWorkingHours().catch(() => null);

      const [response, workingHoursResponse] = await Promise.all([attendancePromise, workingHoursPromise]);
      const whData = workingHoursResponse && workingHoursResponse.data ? workingHoursResponse.data : null;

      if (whData && !workingHoursDataOverride) {
        setWorkingHoursSettings(whData);
      }

      const raw = formatAttendanceData((response && response.data) || []);
      setRawAttendanceRows(raw);
      const computed = whData ? computeDayTypeForItems(raw, whData, approvedHalfDayLookup) : raw;
      applyAttendanceRows(computed, queryOverride);
      writeAttendanceCache(month, year, computed);
      setHasLoadedOnce(true);
    } catch (error) {
      setOriginalData([]);
      setTotalDurationTime([]);
    } finally {
      setIsLoading(false);
      setIsMonthSwitching(false);
    }
  };

  const fetchLeaveBalance = async (month, year) => {
    try {
      if (!authUser.id) {
        setLeaveBalanceSummary(null);
        return;
      }

      const response = await Authapi.getLeaveBalanceByUserId(authUser.id, {
        month: Number(month),
        year: Number(year),
      });

      if (response && response.status) {
        setLeaveBalanceSummary(response.summary || null);
      } else {
        setLeaveBalanceSummary(null);
      }
    } catch (error) {
      setLeaveBalanceSummary(null);
    }
  };

  useEffect(() => {
    let today = new Date();
    let month = today.getMonth() + 1;
    let year = today.getFullYear();

    const cachedRows = readAttendanceCache(month, year);
    if (cachedRows) {
      applyAttendanceRows(cachedRows);
      setHasLoadedOnce(true);
      setIsLoading(false);
      fetchAttendanceData(month, year, workingHoursSettings, "", false);
    } else {
      fetchAttendanceData(month, year);
    }

    fetchLeaveBalance(month, year);

    Authapi.getTickets1().then((data) => {
      setLeaveTickets((data && data.data) || []);
    });

    Authapi.getStatusColors().then((response) => {
      if (response && response.status && response.data) {
        const fetchedColors = response.data;
        const defaultColors = {
          'Present': '#1dbb11',
          'Absent': '#ff5c5c',
          'Half day': '#0acdb6',
          'Left Early': '#065112',
          'Late': '#9d744d',
          'Week Off': '#0f38db',
          'Holiday': '#ca04cd',
          'Short Hours': '#ffffff'
        };

        Object.keys(defaultColors).forEach(key => {
          if (!fetchedColors[key] || (typeof fetchedColors[key] === 'string' && (fetchedColors[key].toLowerCase() === '#ffffff' || fetchedColors[key].toLowerCase() === '#fff'))) {
            fetchedColors[key] = defaultColors[key];
          }
        });

        if (fetchedColors['Comes Late'] && !fetchedColors['Late']) {
          fetchedColors['Late'] = fetchedColors['Comes Late'];
        }
        setStatusColors(fetchedColors);
      } else {
        setStatusColors({});
      }
    });
  }, []);

  useEffect(() => {
    if (!rawAttendanceRows.length || !workingHoursSettings) {
      return;
    }

    const recomputedRows = computeDayTypeForItems(rawAttendanceRows, workingHoursSettings, approvedHalfDayLookup);
    applyAttendanceRows(recomputedRows, searchQuery);
  }, [approvedHalfDayLookup, rawAttendanceRows, workingHoursSettings]);

  const handleMonthChange = async (e) => {
    let select = e.target.value;
    let isCleared = false;
    if (!select || !select.trim()) {
      select = initialMonthYear;
      isCleared = true;
    }

    setSelectedMonthYear(select);

    const parts = select.split('-');
    let year = parseInt(parts[0], 10);
    let month = parseInt(parts[1], 10);

    if (isNaN(year) || isNaN(month)) {
      const today = new Date();
      month = today.getMonth() + 1;
      year = today.getFullYear();
      select = `${year}-${String(month).padStart(2, "0")}`;
      setSelectedMonthYear(select);
    }

    setSearchQuery("");
    setPage(0);
    setSuggestions([]);
    setIsMonthSwitching(true);
    setTotalDurationTime([]);
    const cachedRows = readAttendanceCache(month, year);

    fetchLeaveBalance(month, year);

    if (cachedRows) {
      setTimeout(() => {
        applyAttendanceRows(cachedRows, "");
        setIsMonthSwitching(false);
      }, 250);
      setHasLoadedOnce(true);
      fetchAttendanceData(month, year, workingHoursSettings, "", false);
    } else {
      fetchAttendanceData(month, year, workingHoursSettings, "", true);
    }

    Authapi.getTickets1().then((data) => {
      setLeaveTickets((data && data.data) || []);
    });
  };

  const openMonthPicker = () => {
    const input = monthInputRef.current;
    if (!input) return;

    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }

    input.focus();
    input.click();
  };

  const applySearch = (query) => {
    const normalizedQuery = (query || "").toLowerCase();

    if (!normalizedQuery) {
      setPage(0);
      setTotalDurationTime(originalData);
      return;
    }

    const filteredData = originalData.filter((item) =>
      item.name.toLowerCase().includes(normalizedQuery) ||
      item.firstName.toLowerCase().includes(normalizedQuery)
    );
    setPage(0);
    setTotalDurationTime(filteredData);
  };

  const handleSearch = () => {
    applySearch(searchQuery);
  };

  const handleSuggestionClick = (suggestion) => {
    const nextQuery = suggestion.firstName;
    setSearchQuery(nextQuery);
    setPage(0);
    setSuggestions([]);
    applySearch(nextQuery);
  };

  const handleChange = (e) => {
    const nextQuery = e.target.value;
    const suggestions = originalData.filter((item) => {
      return (
        item.name.toLowerCase().includes(nextQuery.toLowerCase()) ||
        item.firstName.toLowerCase().includes(nextQuery.toLowerCase())
      );
    });
    setSearchQuery(nextQuery);
    setSuggestions(suggestions);
    applySearch(nextQuery);
  };

  var month = new Array();
  month[0] = "01";
  month[1] = "02";
  month[2] = "03";
  month[3] = "04";
  month[4] = "05";
  month[5] = "06";
  month[6] = "07";
  month[7] = "08";
  month[8] = "09";
  month[9] = "10";
  month[10] = "11";
  month[11] = "12";

  var d = new Date();
  var dateString = d.getFullYear() + "-" + month[d.getMonth()];
  const displayMonthYear = selectedMonthYear
    ? new Date(`${selectedMonthYear}-01T00:00:00`).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    })
    : "";
  const totalLeaveBalance = formatLeaveBalanceCount(leaveBalanceSummary?.total_leave_balance ?? 0);
  const usedLeaveBalance = formatLeaveBalanceCount(
    (Number(leaveBalanceSummary?.paid_leave_used ?? 0) || 0) +
    (Number(leaveBalanceSummary?.sick_leave_used ?? 0) || 0)
  );

  const renderDayType = (params) => {
    const authUserId = ls("user").id;

    const leaveDateMatch = leaveTickets.some((ticket) => {
      const [startDate, endDate] = ticket.date_range.split(" to ");
      const start = new Date(startDate.split('-').reverse().join('-'));
      const end = new Date(endDate.split('-').reverse().join('-'));
      const currentDate = new Date(params.row.date.split('-').reverse().join('-'));

      const isMatch = currentDate >= start && currentDate <= end && ticket.user_id === authUserId;
      return isMatch;
    });

    if (params.value === "Left Early" && params.row.userId === authUserId && !leaveDateMatch) {
      const leaveDate = params.row.date;
      return (
        <div style={{ display: "flex", alignItems: "center" }}>
          {params.value}
          <Tooltip title="Add Leave">
            <Link
              to={`/leave-tickets/add?date=${leaveDate}`}
              className="dashboard-daytype-add-link"
              style={{ textDecoration: "none", fontWeight: "bold", marginLeft: "5px", marginTop: "8px" }}
            >
              <MdAddBox size={24} />
            </Link>
          </Tooltip>
        </div>
      );
    }
    return params.value;
  };

  const columns = [
    { field: "name", headerName: "Employee ID", flex: 1, headerClassName: 'header-red' },
    { field: "firstName", headerName: "Name", flex: 1, headerClassName: 'header-red' },
    { field: "date", headerName: "Date", flex: 1, headerClassName: 'header-red' },
    {
      field: "hours",
      headerName: "Hours/Min",
      flex: 1,
      headerClassName: 'header-red',
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {formatDurationText(params.value)}
        </div>
      )
    },
    {
      field: "attendance",
      headerName: "Attendance",
      flex: 1,
      headerClassName: 'header-red',
      renderCell: (params) => {
        let statusText = params.value;
        if (statusText === 'Comes Late') {
          statusText = 'Late';
        }
        const color = statusColors && statusColors[statusText] ? statusColors[statusText] : '#ffffff';
        // return <span style={{ color, fontWeight: 'bold' }}>{statusText}</span>;
        return (
          <div
            style={{
              backgroundColor: color,
              color: getContrastYIQ(color),
              fontWeight: 'bold',
              padding: '1px 10px',
              borderRadius: '5px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              minWidth: '110px', // Increased width
              minHeight: '26px',
              lineHeight: 1.2,
            }}
          >
            {statusText}
          </div>
        );
      }
    },
    { field: "dayType", headerName: "Day-Type", flex: 1, headerClassName: 'header-red', renderCell: renderDayType },
  ];

  const pageSizeOptions = [10, 25, 50];
  if (totalDurationTime.length > 0) {
    pageSizeOptions.push({ value: totalDurationTime.length, label: 'All' });
  }
  if (!pageSizeOptions.some(option => option === pageSize || option.value === pageSize)) {
    pageSizeOptions.push(pageSize);
  }

  const downloadAttendanceXLSX = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Attendance');
    const fillColor = 'FFFFFF00';
    for (let row = 1; row <= 2; row++) {
      for (let col = 1; col <= 5; col++) {
        const cell = worksheet.getCell(row, col);
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: fillColor }
        };
      }
    }

    const base64Image = await getBase64ImageFromUrl('/logo.png');
    const imageId = workbook.addImage({
      base64: base64Image.split(',')[1],
      extension: 'png',
    });
    const columnWidth = worksheet.getColumn(1).width || 10;
    const imageWidth = 50;
    const startCol = (columnWidth - imageWidth / 7.5) / 6;
    worksheet.addImage(imageId, {
      tl: { col: startCol, row: 0 },
      ext: { width: imageWidth, height: 40 }
    });

    worksheet.getCell('B1').value = `Cherrypik Software Attendance Report`;

    // Calculate date range
    let dateRangeText = 'Report';
    if (totalDurationTime.length > 0) {
      const dates = totalDurationTime.map(row => {
        const dateValue = row.date;
        if (!dateValue) return null;
        if (/^\d{2}-\d{2}-\d{4}$/.test(dateValue)) {
          const [day, month, year] = dateValue.split('-');
          return new Date(`${year}-${month}-${day}`);
        }
        return new Date(dateValue);
      }).filter(d => d && !isNaN(d.getTime()));

      if (dates.length > 0) {
        const minDate = new Date(Math.min.apply(null, dates));
        const maxDate = new Date(Math.max.apply(null, dates));

        const formatDate = (date) => {
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}-${month}-${year}`;
        };

        dateRangeText = `Date Range: ${formatDate(minDate)} To ${formatDate(maxDate)}`;
      }
    }

    worksheet.getCell('B2').value = dateRangeText;
    worksheet.getCell('B3').font = { bold: true, size: 14 };

    // Prepare headers
    const columnsFiltered = columns.filter(col => col.field !== 'firstName');
    const headers = columnsFiltered.map(col => col.headerName);
    const dateIndex = headers.indexOf('Date');
    headers.splice(dateIndex + 1, 0, 'Day'); // Insert 'Day' after 'Date'
    const headerRow = worksheet.addRow(headers);

    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF00FF00' },
      };
      cell.font = { bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });

    if (totalDurationTime.length === 0) {
      worksheet.getCell('A3').value = 'No Data Available';
      worksheet.mergeCells('A3:I3');
      worksheet.getCell('A3').alignment = { horizontal: 'center' };
    } else {
      const attendanceColIndex = headers.indexOf('Attendance') + 1; // Excel columns are 1-indexed
      totalDurationTime.forEach(row => {
        const rowData = columnsFiltered.map(col => row[col.field]);
        const dateValue = row['date'];
        let dayValue = '';
        if (dateValue) {
          let jsDate;
          // Handle DD-MM-YYYY format
          if (/^\d{2}-\d{2}-\d{4}$/.test(dateValue)) {
            const [day, month, year] = dateValue.split('-');
            jsDate = new Date(`${year}-${month}-${day}`);
          } else {
            jsDate = new Date(dateValue);
          }
          if (!isNaN(jsDate)) {
            dayValue = jsDate.toLocaleDateString('en-US', { weekday: 'long' });
          }
        }
        rowData.splice(dateIndex + 1, 0, dayValue); // Insert day after date
        const addedRow = worksheet.addRow(rowData);

        let attendanceValue = row['attendance'];
        if (attendanceValue === 'Comes Late') {
          attendanceValue = 'Late';
        }
        const attendanceColor = statusColors && statusColors[attendanceValue]
          ? statusColors[attendanceValue].replace('#', '')
          : '000000';
        if (attendanceColIndex > 0) {
          // Write the modified value to the cell
          addedRow.getCell(attendanceColIndex).value = attendanceValue;
          addedRow.getCell(attendanceColIndex).font = {
            color: { argb: `FF${attendanceColor.toUpperCase()}` },
            bold: true,
          };
        }
      });
    }

    worksheet.columns.forEach(column => {
      column.width = 20;
    });

    const roles = ls.get("roles");
    const userName = ls.get("user").firstName + " " + ls.get("user").lastName;
    const selectedMonthYear = document.getElementById("datetime-local").value;
    const [year, month] = selectedMonthYear.split('-');
    let fileName = '';

    if (roles && roles.includes("Super Admin")) {
      fileName = `Users Attendance ${month}-${year}.xlsx`;
    } else {
      fileName = `${userName} Attendance ${month}-${year}.xlsx`;
    }

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName);
  };

  return (
    <>
        <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
            flexWrap: "wrap",
            gap: "10px"
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              gap: "14px",
              flexWrap: "nowrap",
              flex: "0 0 auto"
            }}
          >
            <fieldset
              className="dashboard-leave-balance dashboard-month-picker-shell"
              style={{
                flex: "0 0 auto",
                border: darkMode ? "1px solid #4a4a4a" : "1px solid var(--hrms-field-light-border)",
                backgroundColor: darkMode ? "#1c1c1c" : "transparent"
              }}
            >
              <legend>Select Month/Year</legend>
              <div
                className="dashboard-month-picker-input-wrap"
                onClick={openMonthPicker}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openMonthPicker();
                  }
                }}
              >
                <div className="dashboard-month-picker-display">
                  <span className="dashboard-month-picker-display-text">{displayMonthYear}</span>
                  <MdCalendarToday className="dashboard-month-picker-display-icon" />
                </div>
                <TextField
                  id="datetime-local"
                  className="month-year-filter"
                  variant="outlined"
                  value={selectedMonthYear}
                  type="Month"
                  onChange={handleMonthChange}
                  inputRef={monthInputRef}
                  InputProps={{
                    style: {
                      accentColor: 'var(--hrms-primary-blue-end, #036dda)',
                      backgroundColor: 'transparent',
                      background: 'transparent',
                    },
                  }}
                  inputProps={{
                    min: "2020-01",
                    max: dateString,
                    style: {
                      accentColor: 'var(--hrms-primary-blue-end, #036dda)',
                      backgroundColor: 'transparent',
                      background: 'transparent',
                      color: 'transparent',
                      WebkitTextFillColor: 'transparent',
                      opacity: 0,
                    }
                  }}
                />
              </div>
            </fieldset>

            <fieldset
              className="dashboard-leave-balance"
              style={{
                flex: "0 0 auto",
                border: darkMode ? "1px solid #4a4a4a" : "1px solid var(--hrms-field-light-border)",
                backgroundColor: darkMode ? "#1c1c1c" : "transparent"
              }}
            >
              <legend>Leave balance</legend>
              <div className="dashboard-leave-balance-content">
                <div className="dashboard-leave-balance-row"><strong>Total Leave :</strong> {totalLeaveBalance}</div>
                <div className="dashboard-leave-balance-row"><strong>Used Leave :</strong> {usedLeaveBalance}</div>
              </div>
            </fieldset>
          </div>

          <TextField
            label="Search"
            value={searchQuery}
            onChange={handleChange}
            variant="outlined"
            InputLabelProps={{
              shrink: searchQuery !== "",
              style: {
                color: darkMode ? '#ffffff' : '#000000',
                transform: searchQuery !== "" ? 'translate(14px, -6px) scale(0.75)' : 'translate(14px, 9px) scale(1)',
                transition: 'transform 0.2s ease-in-out'
              }
            }}
            inputProps={{
              style: { fontWeight: 'normal' }
            }}
            InputProps={{
              style: {
                backgroundColor: darkMode ? '#2a2a2a' : 'lightgray',
                borderRadius: '25px',
                height: "40px",
                color: darkMode ? '#ffffff' : '#000000'
              },
              endAdornment: (
                <InputAdornment position="end">
                  {searchQuery && (
                    <ClearIcon onClick={handleClearSearch} style={{ cursor: 'pointer', color: darkMode ? '#ffffff' : 'inherit' }} />
                  )}
                  <SearchIcon onClick={handleSearch} style={{ cursor: 'pointer', color: darkMode ? '#ffffff' : 'inherit' }} />
                </InputAdornment>
              ),
            }}
          />
        </div>

        {searchQuery && suggestions.length > 0 && (
          <Paper
            style={{
              position: 'absolute',
              right: '114px',
              marginTop: '-5px',
              zIndex: 1,
              width: "207px",
              maxHeight: '200px',
              overflowY: 'auto',
              border: darkMode ? '1px solid #444' : '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: darkMode ? '0 2px 10px rgba(255, 255, 255, 0.1)' : '0 2px 10px rgba(0, 0, 0, 0.1)',
              backgroundColor: darkMode ? '#1e1e1e' : '#ffffff',
              color: darkMode ? '#ffffff' : 'inherit'
            }}>
            {[...new Set(suggestions.map(suggestion => suggestion.firstName))].map((firstName, index) => (
              <MenuItem
                key={index}
                onClick={() => handleSuggestionClick({ firstName })}
                style={{
                  padding: '10px 20px',
                  cursor: 'pointer',
                  color: darkMode ? '#ffffff' : 'inherit'
                }}
              >
                {firstName}
              </MenuItem>
            ))}
          </Paper>
        )}

        <div className="dashboard-attendance-table holiday-table-shell">
          <Box sx={{ width: 1, minWidth: 0, overflow: 'hidden' }}>
            <DataGrid
              className="dashboard-attendance-grid"
              columns={columns}
              rows={totalDurationTime}
              loading={isLoading && (!hasLoadedOnce || isMonthSwitching)}
              page={page}
              pageSize={pageSize}
              rowsPerPageOptions={pageSizeOptions}
              rowHeight={42}
              headerHeight={40}
              autoHeight={true}
              hideFooter
              hideFooterSelectedRowCount={true}
              disableSelectionOnClick
              sx={{
                border: 0,
                backgroundColor: darkMode ? '#1e1e2e' : '#ffffff',
                borderRadius: 0,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: '#036dda',
                  color: '#ffffff',
                  fontWeight: 'bold',
                  fontSize: '15px',
                  borderBottom: 'none',
                },
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: '#036dda',
                  color: '#ffffff',
                  fontWeight: 'bold',
                  borderBottom: 'none',
                },
                "& .MuiDataGrid-cell": {
                  color: darkMode ? '#ffffff' : 'inherit',
                  fontSize: '14px',
                  borderBottom: 'none',
                },
                "& .MuiDataGrid-main": {
                  minHeight: totalDurationTime.length > 0 ? "auto" : "100px !important",
                },
                "& .MuiDataGrid-overlay": {
                  height: totalDurationTime.length > 0 ? "auto" : "100px !important",
                },
                "& .MuiDataGrid-virtualScroller": {
                  minHeight: totalDurationTime.length > 0 ? "auto" : "100px !important",
                }
              }}
              components={{
                NoRowsOverlay: () => (
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    color: darkMode ? '#ffffff' : 'inherit',
                    backgroundColor: darkMode ? '#1e1e1e' : 'inherit'
                  }}>
                    {isLoading || isMonthSwitching ? "Loading..." : "No data found"}
                  </div>
                ),
              }}
              onPageChange={(newPage) => setPage(newPage)}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPage(0);
              }}
              getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? "zebra-table-row" : ""}
            />
          </Box>
          <div
            className="shared-table-bottom-bar"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              visibility: totalDurationTime.length > 0 ? 'visible' : 'hidden',
            }}
          >
            <div className="shared-table-bottom-bar-left">
              <ExportButton
                label="Export"
                onClick={downloadAttendanceXLSX}
              />
            </div>
            <CommonTablePagination
              count={totalDurationTime.length}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(event) => {
                setPageSize(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={pageSizeOptions}
              className="shared-table-bottom-pagination"
              sx={getCommonPaginationSx(darkMode)}
              isDarkMode={darkMode}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Tableforfilter;
